// src/components/Sidebar.jsx
import React, { useEffect, useRef } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaBell, 
  FaUserFriends, 
  FaBriefcase, 
  FaCheckSquare, 
  FaTruck, 
  FaBook,
  FaComments,
  FaBars
} from 'react-icons/fa';

// El asistente de IA es facturable: en prod se compila con REACT_APP_ASSISTANT_ENABLED
// distinto de 'true' y no se muestra en el menú.
const ASSISTANT_ENABLED = process.env.REACT_APP_ASSISTANT_ENABLED === 'true';

const Sidebar = ({ expanded, toggleSidebar, assistantEnabled = ASSISTANT_ENABLED }) => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  
  // Efecto para manejar clics fuera del sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Si el sidebar está expandido y el clic fue fuera del sidebar
      if (expanded && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar();
      }
    };

    // Solo añadir el evento si el sidebar está expandido
    if (expanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Limpiar el listener cuando se desmonta el componente o cambia el estado
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expanded, toggleSidebar]);
  
  return (
    <div
      ref={sidebarRef}
      className="sidebar" // Usamos la clase definida en theme.css
      style={{
        width: expanded ? '200px' : '60px',
        transition: 'width 0.2s ease',
        // Eliminamos la declaración de background-color aquí para usar la variable CSS
      }}
    >
      {/* Botón para expandir/colapsar */}
      <div style={{ 
        padding: '1rem', 
        display: 'flex', 
        justifyContent: expanded ? 'flex-end' : 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <button
          onClick={toggleSidebar}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '1.2rem',
            cursor: 'pointer',
            padding: '0.5rem'
          }}
          aria-label="Toggle Sidebar"
        >
          <FaBars />
        </button>
      </div>

      <Nav className="flex-column">
        <Nav.Link
          as={Link}
          to="/alerts"
          className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname === '/alerts' ? 'active' : ''}`}
          style={{
            opacity: location.pathname === '/alerts' ? 1 : 0.8,
            backgroundColor: location.pathname === '/alerts' ? 'rgba(255,255,255,0.2)' : 'transparent'
          }}
        >
          <FaBell />
          {expanded && <span style={{ marginLeft: '0.8rem' }}>Vencimientos</span>}
        </Nav.Link>

        <Nav.Link 
          as={Link} 
          to="/people" 
          className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname.startsWith('/people') ? 'active' : ''}`}
          style={{
            opacity: location.pathname.startsWith('/people') ? 1 : 0.8,
            backgroundColor: location.pathname.startsWith('/people') ? 'rgba(255,255,255,0.2)' : 'transparent'
          }}
        >
          <FaUserFriends />
          {expanded && <span style={{ marginLeft: '0.8rem' }}>Personas</span>}
        </Nav.Link>

        <Nav.Link 
          as={Link} 
          to="/works" 
          className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname.startsWith('/works') ? 'active' : ''}`}
          style={{
            opacity: location.pathname.startsWith('/works') ? 1 : 0.8,
            backgroundColor: location.pathname.startsWith('/works') ? 'rgba(255,255,255,0.2)' : 'transparent'
          }}
        >
          <FaBriefcase />
          {expanded && <span style={{ marginLeft: '0.8rem' }}>Obras</span>}
        </Nav.Link>

        <Nav.Link 
          as={Link} 
          to="/inspections" 
          className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname.startsWith('/inspections') ? 'active' : ''}`}
          style={{
            opacity: location.pathname.startsWith('/inspections') ? 1 : 0.8,
            backgroundColor: location.pathname.startsWith('/inspections') ? 'rgba(255,255,255,0.2)' : 'transparent'
          }}
        >
          <FaCheckSquare />
          {expanded && <span style={{ marginLeft: '0.8rem' }}>Inspections</span>}
        </Nav.Link>

        <Nav.Link 
          as={Link} 
          to="/tools" 
          className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname.startsWith('/tools') ? 'active' : ''}`}
          style={{
            opacity: location.pathname.startsWith('/tools') ? 1 : 0.8,
            backgroundColor: location.pathname.startsWith('/tools') ? 'rgba(255,255,255,0.2)' : 'transparent'
          }}
        >
          <FaTruck />
          {expanded && <span style={{ marginLeft: '0.8rem' }}>Equipos</span>}
        </Nav.Link>

        <Nav.Link 
          as={Link} 
          to="/trainings" 
          className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname.startsWith('/trainings') ? 'active' : ''}`}
          style={{
            opacity: location.pathname.startsWith('/trainings') ? 1 : 0.8,
            backgroundColor: location.pathname.startsWith('/trainings') ? 'rgba(255,255,255,0.2)' : 'transparent'
          }}
        >
          <FaBook />
          {expanded && <span style={{ marginLeft: '0.8rem' }}>Capacitaciones</span>}
        </Nav.Link>

        {assistantEnabled && (
          <Nav.Link
            as={Link}
            to="/asistente"
            className={`d-flex align-items-center py-3 px-3 text-white ${location.pathname.startsWith('/asistente') ? 'active' : ''}`}
            style={{
              opacity: location.pathname.startsWith('/asistente') ? 1 : 0.8,
              backgroundColor: location.pathname.startsWith('/asistente') ? 'rgba(255,255,255,0.2)' : 'transparent'
            }}
          >
            <FaComments />
            {expanded && <span style={{ marginLeft: '0.8rem' }}>Asistente</span>}
          </Nav.Link>
        )}
      </Nav>
    </div>
  );
};

export default Sidebar;
